import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { dataMapperSelection } from "../dataMapperServices";
import {
  DATA_MAPPER_SCOPES,
  refusalReason,
  type DataMapperScope,
} from "../dataMapper.types";

/** The API's refusals that mean the stored selection no longer exists. */
export const GONE_REASONS = new Set(["profile-not-found", "profile-not-in-client", "client-not-found"]);

/** Bind the tab's selection to the signed-in user (a selection someone else made is dropped). */
export function bindSelectionToSignedInUser(rootStore: RootStore): void {
  const authStore = (
    rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
  ).authStore;
  dataMapperSelection(rootStore)?.bindToUser(authStore?.currentUser?.id);
}

/**
 * The sentence a failed Data Mapper call shows. A refusal that says the open
 * profile is gone drops it from the selection, so the page then says "open a
 * profile" instead of repeating the refusal.
 */
export function editorFailure(rootStore: RootStore, err: unknown, profileId: string | null, fallback: string): string {
  if (GONE_REASONS.has(refusalReason(err) ?? "") && profileId) {
    dataMapperSelection(rootStore)?.forgetProfile(profileId);
    return "The profile that was open no longer exists. Choose one on the Profiles page.";
  }
  return loadErrorMessage(err, fallback);
}

/** Rows grouped by scope, narrowest first, each group sorted by name. */
export function groupByScope<T extends { scope: DataMapperScope; name: string }>(
  rows: readonly T[]
): Array<{ scope: DataMapperScope; rows: T[] }> {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  return DATA_MAPPER_SCOPES.map((scope) => ({ scope, rows: rows.filter((r) => r.scope === scope).sort(byName) }));
}

export const SCOPE_LABELS: Record<DataMapperScope, { title: string; detail: string }> = {
  profile: { title: "This profile", detail: "Saved in the open profile only." },
  client: { title: "This client", detail: "Shared by every profile of this client." },
  global: { title: "Global", detail: "Shared by every client." },
};
